import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { COUNTRIES } from '../context/CountryContext';

// Real accounts shared with the mobile app - see AuthContext. If the user
// arrived here mid-checkout (redirected from TheaterRoom), we send them
// right back to where they were after a successful sign-in/sign-up.
export default function Login() {
  const { t } = useTranslation();
  const { login, signup, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('USA');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(username, password);
      } else {
        await signup({ username, email, password, country });
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <form style={styles.card} onSubmit={handleSubmit}>
        <h1 style={styles.title}>{mode === 'login' ? t('login.signInTitle') : t('login.createAccountTitle')}</h1>
        <p style={styles.subtitle}>{t('login.subtitle')}</p>

        <label style={styles.label}>{t('login.username')}</label>
        <input style={styles.input} value={username} onChange={(e) => setUsername(e.target.value)} required />

        {mode === 'signup' && (
          <>
            <label style={styles.label}>{t('login.email')}</label>
            <input style={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <label style={styles.label}>{t('login.country')}</label>
            <select style={styles.input} value={country} onChange={(e) => setCountry(e.target.value)}>
              {COUNTRIES.filter((c) => c.code).map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.label}</option>
              ))}
            </select>
          </>
        )}

        <label style={styles.label}>{t('login.password')}</label>
        <input style={styles.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />

        {!!error && <p style={styles.error}>{error}</p>}

        <button style={styles.button} type="submit" disabled={loading}>
          {loading ? t('login.pleaseWait') : mode === 'login' ? t('login.signInButton') : t('login.createAccountButton')}
        </button>

        <p style={styles.switchText}>
          {mode === 'login' ? `${t('login.noAccount')} ` : `${t('login.haveAccount')} `}
          <button
            type="button"
            style={styles.switchButton}
            onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
          >
            {mode === 'login' ? t('login.signUp') : t('login.signInLink')}
          </button>
        </p>
        <Link to="/" style={styles.homeLink}>{t('login.backToHome')}</Link>
      </form>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#001f3f',
    padding: '20px',
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(135,206,235,0.25)',
    borderRadius: '16px',
    padding: '32px',
    width: 'min(90vw, 420px)',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    color: '#fff',
    fontSize: '24px',
    fontWeight: 900,
    marginBottom: '6px',
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px',
    textAlign: 'center',
    marginBottom: '20px',
  },
  label: {
    color: '#87CEEB',
    fontSize: '12px',
    fontWeight: 700,
    marginTop: '12px',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(135,206,235,0.25)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontSize: '14px',
  },
  error: {
    color: '#ff6b6b',
    fontSize: '13px',
    marginTop: '12px',
    textAlign: 'center',
  },
  button: {
    marginTop: '20px',
    backgroundColor: '#87CEEB',
    color: '#001f3f',
    fontWeight: 'bold',
    fontSize: '15px',
    border: 'none',
    borderRadius: '10px',
    padding: '12px',
    cursor: 'pointer',
  },
  switchText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px',
    textAlign: 'center',
    marginTop: '16px',
  },
  switchButton: {
    background: 'none',
    border: 'none',
    color: '#87CEEB',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '13px',
    padding: 0,
  },
  homeLink: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '12px',
    textAlign: 'center',
    marginTop: '16px',
    textDecoration: 'none',
  },
};